import { lazy, Suspense, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useCanRender3D } from "../hooks/useCanRender3D";
import { FallbackNetwork } from "./FallbackNetwork";
import { AmbientAudio } from "./AmbientAudio";
import { HERO_NODES } from "../data/content";

const HeroScene = lazy(() => import("./three/HeroScene"));

const LINES = ["Engineering the", "software that moves", "your business forward."];

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const can3d = useCanRender3D();
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const sceneOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const sceneY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const sceneScale = useTransform(scrollYProgress, [0, 1], [1, 0.88]);

  return (
    <section id="top" ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 grid-texture" aria-hidden="true" />
      <div className="absolute inset-0 green-glow-bg" aria-hidden="true" />

      <motion.div
        className="absolute inset-y-0 right-0 hidden w-[58%] md:block"
        aria-hidden={!can3d}
        style={reduce ? undefined : { opacity: sceneOpacity, y: sceneY, scale: sceneScale }}
      >
        {can3d ? (
          <Suspense fallback={null}>
            <HeroScene />
          </Suspense>
        ) : null}
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-28 pb-16 sm:px-8 lg:px-16">
        <div className="max-w-2xl">
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.24em] text-primary font-semibold"
            data-testid="hero-eyebrow"
          >
            Farmint — Software Engineering & Technology
          </motion.p>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.06]">
            <MaskedLine delay={0.25} reduce={reduce}>Engineering the</MaskedLine>
            <MaskedLine delay={0.37} reduce={reduce}>software that moves</MaskedLine>
            <MaskedLine delay={0.49} reduce={reduce}>
              your business <span className="text-primary">forward.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
            data-testid="hero-subtext"
          >
            Farmint helps startups and enterprises design, build, deploy and scale
            enterprise-grade software, SaaS platforms, mobile applications and
            AI-powered solutions.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-all duration-200 hover:brightness-110 hover:shadow-[0_0_36px_var(--green-glow)]"
            >
              Start a Conversation
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border hairline px-7 py-3.5 text-sm font-semibold text-foreground transition-all duration-200 hover:border-primary/50 hover:text-primary"
            >
              Explore Our Services
            </a>
          </motion.div>

          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 font-mono text-xs tracking-[0.14em] text-muted-foreground"
            data-testid="hero-pipeline"
          >
            idea <span className="text-primary">→</span> architecture <span className="text-primary">→</span> engineering{" "}
            <span className="text-primary">→</span> deployment <span className="text-primary">→</span> scale
          </motion.p>
        </div>

        {!can3d && (
          <div className="mt-12 md:hidden">
            <FallbackNetwork labels={HERO_NODES.map((n) => n.name)} className="mx-auto h-72 w-72 text-foreground" />
          </div>
        )}
      </div>

      <motion.a
        href="#experience"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-muted-foreground md:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.a>
      <AmbientAudio />
    </section>
  );
};
